import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { http, eur, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/site/Logo";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { RequestsTable, OrdersTable } from "@/components/admin/AdminTables";
import { ProductsManager } from "@/components/admin/ProductsManager";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { CustomersManager } from "@/components/admin/CustomersManager";
import { CategoriesManager } from "@/components/admin/CategoriesManager";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-3xl border border-fg/10 bg-[var(--ink-2)] p-6">
    <p className="font-mono-ui text-xs uppercase tracking-widest text-mist">{label}</p>
    <p className="mt-2 font-display text-4xl font-semibold" data-testid={testid}>{value}</p>
  </div>
);

export default function Admin() {
  const { user, setUser, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ requests: [], orders: [], stats: {} });
  const [orderDel, setOrderDel] = useState(null);
  const [reqDel, setReqDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, o, s] = await Promise.all([http.get("/admin/requests"), http.get("/admin/orders"), http.get("/admin/stats")]);
      setData({ requests: Array.isArray(r.data) ? r.data : [], orders: Array.isArray(o.data) ? o.data : [], stats: s.data || {} });
    } catch (e) {
      if (e?.response?.status === 401) setUser(false);
      else toast.error(errMsg(e));
    }
  }, [setUser]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const updateStatus = async (id, status) => {
    try { await http.patch(`/admin/requests/${id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteOrder = async () => {
    try { await http.delete(`/admin/orders/${orderDel.id}`); toast.success("Ordine eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
    setOrderDel(null);
  };

  const deleteRequest = async () => {
    try { await http.delete(`/admin/requests/${reqDel.id}`); toast.success("Richiesta eliminata"); load(); } catch (e) { toast.error(errMsg(e)); }
    setReqDel(null);
  };

  if (user === null) return <div className="min-h-screen" />;
  if (!isAdmin) return <AdminLogin denied={!!user} />;

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-5 py-10 sm:px-8" data-testid="admin-dashboard">
      <header className="flex items-center justify-between">
        <Link to="/" data-testid="admin-home-link"><Logo /></Link>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-fg/20 px-4 py-2 text-sm hover:bg-fg/5"><LogOut className="h-4 w-4" /> Esci</button>
      </header>
      <h1 className="mt-12 font-display text-5xl font-semibold">Pannello</h1>
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat label="Richieste nuove" value={data.stats?.requests_new ?? 0} testid="admin-stat-new" />
        <Stat label="Richieste totali" value={data.stats?.requests_total ?? 0} testid="admin-stat-requests" />
        <Stat label="Ordini pagati" value={data.stats?.orders_paid ?? 0} testid="admin-stat-orders" />
        <Stat label="Incassato" value={eur(data.stats?.revenue ?? 0)} testid="admin-stat-revenue" />
        <Stat label="Clienti registrati" value={data.stats?.customers_total ?? 0} testid="admin-stat-customers" />
      </div>
      <Tabs defaultValue="requests" className="mt-10">
        <TabsList className="flex h-auto w-fit max-w-full flex-wrap justify-start rounded-3xl bg-[var(--ink-2)] p-1">
          <TabsTrigger value="requests" data-testid="admin-requests-tab" className="rounded-full px-5">Richieste</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-orders-tab" className="rounded-full px-5">Ordini</TabsTrigger>
          <TabsTrigger value="customers" data-testid="admin-customers-tab" className="rounded-full px-5">Clienti</TabsTrigger>
          <TabsTrigger value="products" data-testid="admin-products-tab" className="rounded-full px-5">Prodotti</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-categories-tab" className="rounded-full px-5">Categorie</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-settings-tab" className="rounded-full px-5">Impostazioni</TabsTrigger>
        </TabsList>
        <TabsContent value="requests"><RequestsTable rows={data.requests} onStatus={updateStatus} onDelete={setReqDel} /></TabsContent>
        <TabsContent value="orders"><OrdersTable rows={data.orders} onDelete={setOrderDel} /></TabsContent>
        <TabsContent value="customers"><CustomersManager onChange={load} /></TabsContent>
        <TabsContent value="products"><ProductsManager /></TabsContent>
        <TabsContent value="categories"><CategoriesManager /></TabsContent>
        <TabsContent value="settings"><SettingsPanel /></TabsContent>
      </Tabs>
      <ConfirmDialog open={!!reqDel} testid="admin-request-delete-dialog" onCancel={() => setReqDel(null)} onConfirm={deleteRequest}
        title={`Eliminare la richiesta di ${reqDel?.name || ""}?`} description="La richiesta verrà cancellata definitivamente dal pannello." />
      <ConfirmDialog open={!!orderDel} testid="admin-order-delete-dialog" onCancel={() => setOrderDel(null)} onConfirm={deleteOrder}
        title="Eliminare questo ordine in attesa?" description="L'ordine verrà cancellato e il link di pagamento Stripe, se ancora aperto, verrà chiuso." />
    </main>
  );
}
